import * as appActions from 'domain/App/actions'
import * as notificationsActions from 'domain/Notifications/actions'
import * as userActions from 'domain/User/actions'
import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { bindActionCreators } from 'redux'
import browserNotificationPermissionStatuses from 'constants/browserNotificationPermissionStatuses'
import { connect } from 'react-redux'
import every from 'lodash/every'
import find from 'lodash/find'
import first from 'lodash/first'
import isEqual from 'lodash/isEqual'
import merge from 'lodash/merge'
import news from 'global/news'
import { NOTIFICATIONS_POLLING_INTERVAL } from 'global/settings'
import paths from 'constants/paths'
import propTypes from 'global/propTypes'
import PropTypes from 'prop-types'
import { selectors } from 'domain'
import userRoles from 'constants/userRoles'

import Navbar from './Navbar'

const NavbarContainer = ({
  isNotificationsBarOpen,
  isUserSSO,
  requestNotifications,
  seenNewsIds,
  setBrowserNotificationPermissionStatus,
  setIsNotificationsBarOpen,
  setNewsIdsSeen,
  signedInAs,
  signOut,
  totalNoOfNewNotifications,
  userModule,
  userRole,
}) => {
  const navigate = useNavigate()
  const location = useLocation()

  const [hasUnseenNews, setHasUnseenNews] = useState(false)

  const [shouldShowWhatsNewNotification, setShouldShowWhatsNewNotification] =
    useState(false)

  const [
    shouldShowEnableNotificationsPrompt,
    setShouldShowEnableNotificationsPrompt,
  ] = useState(false)

  const onCancelEnableNotificationsPrompt = () =>
    setShouldShowEnableNotificationsPrompt(false)

  useEffect(() => {
    if (!signedInAs) {
      navigate(paths.login)
    }
  }, [navigate, signedInAs])

  // Handles dashboard update notifications
  useEffect(() => {
    if (!signedInAs) {
      return
    }

    if (isEqual(location.pathname, paths.whatIsNew)) {
      setHasUnseenNews(false)
    } else {
      const newsForUser = news.filter(item => item.userRoles.includes(userRole))
      const currentNewsIds = newsForUser.map(item => item.id)

      setHasUnseenNews(!every(currentNewsIds, id => seenNewsIds.includes(id)))
      setShouldShowWhatsNewNotification(
        !find(seenNewsIds, newsId => newsId === first(currentNewsIds))
      )
    }
  }, [location, signedInAs, userRole, seenNewsIds])

  const shouldShowPortCallNotifications = userRole !== userRoles.SHIP

  // Handles requesting port call notifications
  useEffect(() => {
    if (shouldShowPortCallNotifications) {
      requestNotifications({})

      const requestInterval = setInterval(() => {
        requestNotifications({})
      }, NOTIFICATIONS_POLLING_INTERVAL)

      return () => clearInterval(requestInterval)
    }
  }, [requestNotifications, shouldShowPortCallNotifications])

  const closeWhatsNewNotification = () =>
    setShouldShowWhatsNewNotification(false)

  const toggleNotificationsBarOpen = () =>
    setIsNotificationsBarOpen(!isNotificationsBarOpen)

  // Handles checking and requesting browser notification permission
  useEffect(() => {
    if (userRole === userRoles.SHIP) {
      return
    }

    const areNotificationsSupported = !!('Notification' in window)
    if (!areNotificationsSupported) {
      setBrowserNotificationPermissionStatus(
        browserNotificationPermissionStatuses.NOT_SUPPORTED
      )

      return
    }

    setBrowserNotificationPermissionStatus(Notification.permission)

    const hasShownPromptThisSession = !!JSON.parse(
      sessionStorage.getItem('shown-enable-notifications-prompt')
    )

    if (
      hasShownPromptThisSession ||
      Notification.permission ===
        browserNotificationPermissionStatuses.GRANTED ||
      Notification.permission === browserNotificationPermissionStatuses.DENIED
    ) {
      return
    }

    setShouldShowEnableNotificationsPrompt(true)
    sessionStorage.setItem('shown-enable-notifications-prompt', true)
  }, [setBrowserNotificationPermissionStatus, userRole])

  const requestNotificationPermission = async () =>
    Notification.requestPermission().then(permission => {
      setBrowserNotificationPermissionStatus(permission)
      setShouldShowEnableNotificationsPrompt(false)
    })

  return (
    signedInAs && (
      <Navbar
        closeWhatsNewNotification={closeWhatsNewNotification}
        hasUnseenNews={hasUnseenNews}
        isUserSSO={isUserSSO}
        onCancelEnableNotificationsPrompt={onCancelEnableNotificationsPrompt}
        onConfirmEnableNotificationsPrompt={requestNotificationPermission}
        onNotificationsButtonClicked={toggleNotificationsBarOpen}
        setNewsIdsSeen={setNewsIdsSeen}
        shouldShowPortCallNotifications={shouldShowPortCallNotifications}
        shouldShowWhatsNewNotification={shouldShowWhatsNewNotification}
        signOut={signOut}
        signedInAs={signedInAs}
        totalNoOfNewNotifications={totalNoOfNewNotifications}
        userModule={userModule}
        userRole={userRole}
        shouldShowEnableNotificationsPrompt={
          shouldShowEnableNotificationsPrompt
        }
      />
    )
  )
}

NavbarContainer.defaultProps = {
  signedInAs: null,
  userModule: null,
  userRole: null,
}

NavbarContainer.propTypes = {
  isNotificationsBarOpen: PropTypes.bool.isRequired,
  isUserSSO: PropTypes.bool.isRequired,
  requestNotifications: PropTypes.func.isRequired,
  seenNewsIds: PropTypes.array.isRequired,
  setBrowserNotificationPermissionStatus: PropTypes.func.isRequired,
  setIsNotificationsBarOpen: PropTypes.func.isRequired,
  setNewsIdsSeen: PropTypes.func.isRequired,
  signOut: PropTypes.func.isRequired,
  signedInAs: PropTypes.string,
  totalNoOfNewNotifications: PropTypes.number.isRequired,
  userModule: PropTypes.arrayOf(PropTypes.string),
  userRole: propTypes.userRole,
}

const mapStateToProps = state => ({
  isNotificationsBarOpen: selectors.isNotificationsBarOpenSelector(state),
  isUserSSO: selectors.isUserSSOSelector(state),
  seenNewsIds: selectors.seenNewsIdsSelector(state),
  signedInAs: selectors.signedInAsSelector(state),
  totalNoOfNewNotifications: selectors.totalNoOfNewNotificationsSelector(state),
  userModule: selectors.userModuleSelector(state),
  userRole: selectors.userRoleSelector(state),
})

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    merge({}, appActions, userActions, notificationsActions),
    dispatch
  )

export default connect(mapStateToProps, mapDispatchToProps)(NavbarContainer)
